import React from "react";
import { ArrowLeft, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LAST_UPDATED = "April 18, 2026";

const Section = ({ title, children }) => (
  <div className="mb-6">
    <h2 className="text-base font-bold text-gray-900 mb-2">{title}</h2>
    <div className="text-sm text-gray-600 leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <FileText className="h-5 w-5 text-green-600" />
        <h1 className="font-bold text-gray-900">Terms of Service</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6" data-testid="terms-of-service-page">
        <p className="text-xs text-gray-400 mb-6">Last updated: {LAST_UPDATED}</p>

        <Section title="1. Acceptance of Terms">
          <p>
            By accessing or using Foodz Lizts ("the Service"), you agree to be bound by these Terms of Service.
            If you do not agree to these terms, do not use the Service.
          </p>
        </Section>

        <Section title="2. Description of Service">
          <p>
            Foodz Lizts is a grocery shopping assistant that provides AI-powered meal planning, recipe suggestions,
            weekly deal browsing, community deal sharing, budget tracking, receipt scanning, and grocery list
            management. The Service is intended for personal, non-commercial use.
          </p>
        </Section>

        <Section title="3. User Accounts">
          <p>You are responsible for maintaining the confidentiality of your account credentials (email, password, and PIN). You agree to provide accurate information and to update it as needed. You are responsible for all activity under your account.</p>
          <p>You must be at least 13 years old to create an account. By creating an account, you represent that you meet this age requirement.</p>
          <p>Providing a phone number is optional and may be used for account recovery purposes in the future.</p>
        </Section>

        <Section title="4. Subscriptions & Billing">
          <p><strong>Free Trial:</strong> New users receive a <strong>14-day free trial</strong> with full access to all premium features. No payment is required to start the trial.</p>
          <p><strong>Post-Trial Free Access:</strong> When the 14-day trial ends, you can continue using Foodz Lizts for free with limited access — the Home tab, the Scan tab (feedback &amp; support), the Grocery List Saver, and the Community Deals sub-tab under Deals remain free to use. Premium features (Price Compare / weekly cost analysis, Budget Friendly, Recipes, Meal Plans, Budget, and Favorites) require a paid subscription.</p>
          <p><strong>Paid Plans:</strong> Plans are available on monthly ($4.99/month) or annual ($45.00/year) billing cycles.</p>
          <p><strong>Payment Processing:</strong> All payments are processed securely through PayPal. You may pay with your PayPal account, PayPal Credit, or a debit/credit card through PayPal's checkout. We do not store your payment details.</p>
          <p><strong>Auto-Renewal:</strong> Subscriptions automatically renew at the end of each billing cycle unless cancelled. You will be charged the applicable subscription fee at the beginning of each renewal period.</p>
          <p><strong>Promotional Discounts:</strong> From time to time, we may offer promotional discount codes. Promo codes are single-use, may have expiration dates, and apply only to the first billing cycle unless otherwise stated. Promo codes cannot be combined with other offers.</p>
          <p><strong>Cancellation:</strong> You may cancel your subscription at any time from the Settings page or by contacting support. Upon cancellation, you will retain access to premium features until the end of your current billing period, after which your account will revert to the free plan described above.</p>
        </Section>

        <Section title="5. Refund Policy">
          <p><strong>14-Day Money-Back Guarantee:</strong> If you are not satisfied with your subscription, you may request a full refund within 14 days of your initial payment. Refund requests made after 14 days will not be honored.</p>
          <p><strong>How to Request a Refund:</strong> Contact us at <strong>support@foodzlizts.com</strong> with your account email and reason for the refund. Refunds are processed within 5-10 business days via PayPal to your original payment method.</p>
          <p><strong>Exceptions:</strong> Refunds are not available for partial billing periods after the 14-day money-back window. If you cancel mid-cycle, you retain access until the end of your paid period but no prorated refund is issued.</p>
          <p><strong>Free Trial:</strong> No charges are incurred during the 14-day free trial. If you cancel before the trial ends, you will not be charged.</p>
        </Section>

        <Section title="6. Community Deals & User Content">
          <p>
            The Service includes a community-driven deal sharing feature where users can post deals,
            comment, like, save, and share content. By posting content, you agree to the following:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>You own or have the right to share the content you post</li>
            <li>Your content is accurate and not intentionally misleading</li>
            <li>You will not post offensive, abusive, or inappropriate content</li>
            <li>User-generated content remains your property but is licensed to Foodz Lizts for display within the Service</li>
            <li>We reserve the right to remove content and moderate posts at our discretion</li>
            <li>Community deal verification is crowd-sourced and does not guarantee accuracy</li>
          </ul>
        </Section>

        <Section title="7. Referral & Rewards Program">
          <p><strong>Referral Program:</strong> Users may earn referral credits by inviting new users who subscribe to a paid plan. You earn $1.00 per month for each referred user who maintains an active subscription.</p>
          <p><strong>Payouts:</strong> Referral earnings can be cashed out via PayPal once your balance reaches $50.00 or more. Payouts are sent to your PayPal email address.</p>
          <p><strong>Gamification:</strong> The Service includes a points-based gamification system where you earn points for community engagement (posting deals, verifying deals, etc.). Points and rankings are for engagement purposes. Top contributors may earn subscription discounts based on community activity tiers.</p>
          <p><strong>Abuse Policy:</strong> Fraudulent referrals, fake accounts, or manipulation of the rewards system will result in forfeiture of earnings and possible account termination.</p>
        </Section>

        <Section title="8. Location Services">
          <p>
            The Service may request access to your device's location services. Location data is used to:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Find nearby grocery stores when posting deals</li>
            <li>Filter community deals by distance from your location</li>
            <li>Provide directions to stores via Google Maps</li>
          </ul>
          <p>
            Location access is optional and requires your explicit consent. You can enable or disable
            location services at any time through your device settings. Your precise location is never
            stored on our servers or shared with other users. See our Privacy Policy for full details.
          </p>
        </Section>

        <Section title="9. Third-Party Data & Accuracy Disclaimer">
          <p>
            The Service displays weekly grocery deals, product prices, store availability, and promotional offers
            sourced from third-party data providers. <strong>This information is provided "as is" for informational
            purposes only.</strong>
          </p>
          <p>
            <strong>We do not guarantee the accuracy, completeness, timeliness, or reliability of any third-party
            data displayed in the app.</strong> Prices, deals, and product availability may vary by location and are
            subject to change without notice. Always verify pricing and availability directly with the retailer
            before making purchasing decisions.
          </p>
          <p>
            Foodz Lizts is not affiliated with, endorsed by, or sponsored by any of the grocery retailers
            whose deals are displayed in the Service.
          </p>
        </Section>

        <Section title="10. Health & Dietary Disclaimer">
          <p>
            Meal plans, recipe suggestions, and dietary filtering provided by the Service are for
            <strong> informational purposes only and do not constitute medical or nutritional advice.</strong>
          </p>
          <p>
            While we make reasonable efforts to filter recipes based on your stated dietary preferences and
            allergies, <strong>we cannot guarantee that all allergens or dietary restrictions are accurately
            identified or excluded.</strong> AI-powered suggestions may contain errors.
          </p>
          <p>
            <strong>If you have severe food allergies or medical dietary requirements, always verify all
            ingredients before consuming any meal.</strong> Foodz Lizts is not responsible for allergic reactions,
            adverse health effects, or any harm resulting from reliance on the Service's dietary suggestions.
          </p>
          <p>Consult a qualified healthcare professional for personalized dietary advice.</p>
        </Section>

        <Section title="11. User Conduct">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Use the Service for any unlawful purpose</li>
            <li>Submit false, misleading, or fraudulent information (including community deals)</li>
            <li>Attempt to gain unauthorized access to other users' accounts</li>
            <li>Interfere with or disrupt the Service or its infrastructure</li>
            <li>Scrape, copy, or redistribute content from the Service for commercial purposes</li>
            <li>Manipulate the gamification, referral, or rewards system through fraudulent activity</li>
            <li>Post spam, advertisements, or solicitations in community deals</li>
            <li>Impersonate another user or entity</li>
          </ul>
          <p>Violation of these terms may result in account suspension or termination and forfeiture of referral earnings.</p>
        </Section>

        <Section title="12. Intellectual Property">
          <p>
            The Service, including its design, code, features, AI models, and branding, is the property of Foodz Lizts
            and is protected by copyright and intellectual property laws. You may not copy, modify, distribute,
            or create derivative works based on the Service without our written permission.
          </p>
          <p>
            User-generated content (community deals, comments) remains the property of the user but is licensed
            to Foodz Lizts for display within the Service.
          </p>
        </Section>

        <Section title="13. Limitation of Liability">
          <p>
            To the maximum extent permitted by law, Foodz Lizts and its operators shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages, including but not limited to
            loss of profits, data, or savings, arising from your use of the Service.
          </p>
          <p>
            Our total liability for any claim arising from or related to the Service shall not exceed the
            amount you paid to us in the 12 months preceding the claim.
          </p>
        </Section>

        <Section title="14. Account Termination">
          <p>
            You may delete your account at any time from the Settings page. We may suspend or terminate
            your account if you violate these Terms. Upon termination, your right to use the Service
            ceases immediately and your data will be permanently deleted.
          </p>
          <p>
            If your account is terminated for Terms violations, any pending referral earnings
            will be forfeited.
          </p>
        </Section>

        <Section title="15. Changes to Terms">
          <p>
            We reserve the right to modify these Terms at any time. Material changes will be communicated
            via email, push notification, or in-app notice. Your continued use of the Service after changes
            constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="16. Governing Law">
          <p>
            These Terms are governed by the laws of the United States. Any disputes arising from these
            Terms or the Service shall be resolved in the courts of the state in which Foodz Lizts operates.
          </p>
        </Section>

        <Section title="17. Contact Us">
          <p>
            For questions about these Terms, refund requests, or legal inquiries, contact us at:<br />
            <strong>Email:</strong> support@foodzlizts.com
          </p>
        </Section>
      </div>
    </div>
  );
}
